import { useEffect, useState } from "react";
import {
  Paper,
  Group,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Stack,
} from "@mantine/core";
import { IconArrowRight, IconEdit, IconX, IconRobot } from "@tabler/icons-react";
import { getFlows } from "../api";

/**
 * Preview component showing agent-type tools inline in the flow editor.
 * Displays each agent tool as "[Tool Name] → [Target Flow Name]" with depth badge.
 */
export default function AgentToolsPreview({
  selectedToolIds = [],
  allTools = [],
  onRemoveTool,
  disabled = false,
}) {
  const [, setFlows] = useState([]);
  const [flowMap, setFlowMap] = useState({});

  // Fetch flows to resolve agentTemplateId to flow names
  useEffect(() => {
    getFlows().then((data) => {
      setFlows(data);
      const map = {};
      data.forEach((flow) => {
        map[flow.id] = flow;
      });
      setFlowMap(map);
    });
  }, []);

  // Filter for agent-type tools only
  const agentTools = allTools.filter(
    (tool) =>
      selectedToolIds?.includes(tool.id) &&
      (tool.toolType === "agent" || tool.type === 2) // Handle both string and enum values
  );

  if (agentTools.length === 0) {
    return null;
  }

  return (
    <Stack gap="xs" mt="xs">
      <Group gap="xs">
        <IconRobot size={14} color="gray" />
        <Text size="xs" fw={500} c="dimmed" tt="uppercase">
          Sub-Agents ({agentTools.length})
        </Text>
      </Group>
      {agentTools.map((tool) => {
        const targetFlow = flowMap[tool.agentTemplateId];
        const flowName = targetFlow?.title || targetFlow?.name || "Unknown Flow";
        const maxDepth = tool.maxAgentDepth || 5;

        return (
          <Paper
            key={tool.id}
            withBorder
            p="xs"
            radius="sm"
            bg="grape.0"
          >
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" style={{ overflow: "hidden" }}>
                <Badge size="sm" variant="light" color="grape">
                  Agent
                </Badge>
                <Text size="sm" fw={500} truncate>
                  {tool.name}
                </Text>
                <IconArrowRight size={14} color="gray" />
                <Text size="sm" c="dimmed" truncate>
                  {flowName}
                </Text>
                <Tooltip label="Maximum recursion depth for this agent">
                  <Badge size="xs" variant="outline" color="gray">
                    depth: {maxDepth}
                  </Badge>
                </Tooltip>
              </Group>
              <Group gap={4} wrap="nowrap">
                <Tooltip label="Edit tool configuration">
                  <ActionIcon
                    component="a"
                    href={`/tools/${tool.id}`}
                    variant="subtle"
                    color="gray"
                    size="sm"
                  >
                    <IconEdit size={14} />
                  </ActionIcon>
                </Tooltip>
                {!disabled && onRemoveTool && (
                  <Tooltip label="Remove from this template">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => onRemoveTool(tool.id)}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}
